import React, { useContext, useState, useEffect } from "react";
import { useLocation, useNavigate, NavLink } from "react-router-dom";
import "./Header.scss";
import { IoMdClose } from "react-icons/io";
import { CiLogout, CiLogin } from "react-icons/ci";
import { userContext } from "../../context/Auth";
import axios from "axios";
import APP_BASE_URL from "../Api/globalApi";
import { CgProfile } from "react-icons/cg";
import { FaShoppingCart } from "react-icons/fa";
import { CartContext } from "../../context/CartContext";

const realStoredValue = (value) => {
  if (!value || value === "null" || value === "undefined") return "";
  return value;
};

const Header = () => {
  const { user, mallLogin } = useContext(userContext);
  const { cartItemCount } = useContext(CartContext);
  const location = useLocation();
  const navigate = useNavigate();
  const isRegisterPage =
    location.pathname === "/" ||
    location.pathname === "/login" ||
    location.pathname === "/forgot" ||
    location.pathname === "/register" ||
    location.pathname === "/otp";
  const isVirtualMallPage =
    location.pathname === "/virtual_mall_login" ||
    location.pathname === "/virtual_mall_dash" ||
    location.pathname === "/virtual_mall_forget_password" ||
    location.pathname === "/VirtualMallWithdraw";
  const [isChecked, setIsChecked] = useState(false);
  
  // Initialize states directly from localStorage to avoid flicker
  const [referralId, setReferralId] = useState(() => {
    const stored = realStoredValue(localStorage.getItem("referral_id"));
    if (stored) return stored;
    const storedData = JSON.parse(localStorage.getItem("data") || "{}");
    const fromData = realStoredValue(storedData.user_referral_id);
    if (fromData) return fromData;
    return realStoredValue(localStorage.getItem("virtual_referal_id"));
  });

  const [userName, setUserName] = useState(() => {
    const stored = localStorage.getItem("user_name");
    if (stored) return stored;
    const storedData = JSON.parse(localStorage.getItem("data") || "{}");
    if (storedData.user_name) return storedData.user_name;
    return localStorage.getItem("virtual_name") || "";
  });

  const [userId, setUserId] = useState(() => localStorage.getItem("user_id") || "");
  const [userApiKey, setUserApiKey] = useState(() => localStorage.getItem("user_api_key") || "");
  const [sponserId, setSponserId] = useState(() => {
    const stored = localStorage.getItem("sponserId");
    if (stored) return stored;
    const storedData = JSON.parse(localStorage.getItem("data") || "{}");
    return storedData.user_sponser_id || "";
  });
  const virtualUserId = realStoredValue(localStorage.getItem("virtual_user_id"));
  const virtualToken = realStoredValue(localStorage.getItem("virtualmall_token"));
  const isVirtualMallLoggedIn = Boolean(virtualUserId && virtualToken);
  const isMainUserLoggedIn = Boolean(realStoredValue(localStorage.getItem("user_id")));
  const isAnyUserLoggedIn = isMainUserLoggedIn || isVirtualMallLoggedIn;

  const mainReferralId =
    realStoredValue(user && user.data && user.data.user_referral_id) ||
    realStoredValue(localStorage.getItem("referral_id")) ||
    realStoredValue(JSON.parse(localStorage.getItem("data") || "{}").user_referral_id) ||
    realStoredValue(referralId);
  const virtualReferralId = realStoredValue(localStorage.getItem("virtual_referal_id"));
  const currentReferralId = isVirtualMallPage
    ? (virtualReferralId || mainReferralId || "N/A")
    : (mainReferralId || virtualReferralId || "N/A");

  const storedName = (value) => {
    if (!value || value === "null" || value === "undefined") return "";
    return value;
  };

  const currentUserName = 
    storedName(user && user.data && user.data.user_name) || 
    storedName(localStorage.getItem("user_name")) || 
    storedName(localStorage.getItem("virtual_name")) || 
    storedName(JSON.parse(localStorage.getItem("data") || "{}").user_name) ||
    storedName(userName) || 
    "User";

  useEffect(() => {
    // Retrieve stored values from localStorage
    const rawReferralId = localStorage.getItem("referral_id");
    if (rawReferralId && !realStoredValue(rawReferralId)) {
      localStorage.removeItem("referral_id");
    }
    const storedReferralId = realStoredValue(rawReferralId);
    const storedUserName = localStorage.getItem("user_name");
    const storedUserId = localStorage.getItem("user_id");
    const storedUserApiKey = localStorage.getItem("user_api_key");
    const storedSponserId = localStorage.getItem("sponserId");

    // Fallback for main user data from 'data' object if individual keys missing
    const storedData = JSON.parse(localStorage.getItem("data") || "{}");

    if (storedReferralId) setReferralId(storedReferralId);
    else if (realStoredValue(storedData.user_referral_id)) setReferralId(storedData.user_referral_id);

    if (storedUserName) setUserName(storedUserName);
    else if (storedData.user_name) setUserName(storedData.user_name);

    if (storedSponserId) setSponserId(storedSponserId);
    else if (storedData.user_sponser_id) setSponserId(storedData.user_sponser_id);

    if (storedUserId) setUserId(storedUserId);
    if (storedUserApiKey) setUserApiKey(storedUserApiKey);

    // ✅ Fallback for Virtual Mall users if main user is not logged in
    if (!storedUserId && !storedData.user_id) {
      const vName = realStoredValue(localStorage.getItem("virtual_name"));
      const vReferral = realStoredValue(localStorage.getItem("virtual_referal_id"));
      if (vName) setUserName(vName);
      if (vReferral) setReferralId(vReferral);
    }

    // Store values in localStorage when user data changes
    if (user && user.data) {
      const { user_referral_id, user_name, user_id, user_api_key, user_sponser_id } = user.data;
      if (realStoredValue(user_referral_id)) {
        localStorage.setItem("referral_id", user_referral_id);
        setReferralId(user_referral_id);
      }
      localStorage.setItem("user_name", user_name);
      localStorage.setItem("user_id", user_id);
      localStorage.setItem("user_api_key", user_api_key);
      localStorage.setItem("sponserId", user_sponser_id);
      localStorage.setItem("data", JSON.stringify(user.data)); // Sync with Home.jsx

      setSponserId(user_sponser_id);
      setUserName(user_name);
      setUserId(user_id);
      setUserApiKey(user_api_key);
    }
  }, [user]);

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  const handleMenuClose = () => {
    setIsChecked(false);
  };

  const logOut = async () => {
    if (isVirtualMallLoggedIn && !isMainUserLoggedIn) {
      localStorage.removeItem("virtualmall_token");
      localStorage.removeItem("virtual_referal_id");
      localStorage.removeItem("virtual_user_id");
      localStorage.removeItem("virtual_name");
      localStorage.removeItem("pendingCartItem");
      setIsChecked(false);
      window.location.replace("/virtual_mall_login");
      return;
    }

    try {
      const response = await axios.post(
        `${APP_BASE_URL}/user/logout`,
        {},
        {
          headers: {
            "user-id": userId,
            "api-key": userApiKey,
            user_id: userId,
            api_key: userApiKey,
          },
        }
      );
      if (response.data.result === true) {
        localStorage.removeItem("referral_id");
        localStorage.removeItem("user_name");
        // localStorage.removeItem("user_id");
        // localStorage.removeItem("user_api_key");
        localStorage.clear();
        setIsChecked(false);
        window.location.replace("/");
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="container-fluid header">
      <input
        type="checkbox"
        checked={isChecked}
        id="checkbox"
        onChange={handleCheckboxChange}
        style={{ display: "none" }}
      />

      <div className="header-left">
        {!isRegisterPage && isAnyUserLoggedIn && (
          <img
            src="/images/ham.png"
            alt="hamburger icon"
            className="ham"
            onClick={handleCheckboxChange}
          />
        )}
        <div className="logo-wrapper">
          <img src="/images/main_logo.png" alt="logo" className="logo" />
        </div>
        {!isRegisterPage && isAnyUserLoggedIn && (
          <div className="user-info">
            <h5>User Id: <strong>{currentReferralId}</strong></h5>
            <h5>Name: <strong>{currentUserName}</strong></h5>
          </div>
        )}
      </div>

      <div className="header-right">
        <div className="header-actions">
          {!isRegisterPage && isAnyUserLoggedIn && (
            <NavLink to="/cart" className="cart-icon">
              <FaShoppingCart size={20} />
              <span className="cart-badge">
                {cartItemCount}
              </span>
            </NavLink>
          )}

          {!isRegisterPage && (
            isAnyUserLoggedIn ? (
              <NavLink id="logout" onClick={logOut} className="auth-link">
                <CiLogout size={22} />
                <h5>Logout</h5>
              </NavLink>
            ) : (
              <NavLink id="login" to={`/virtual_mall_login?redirect=${location.pathname}`} className="auth-link">
                <CiLogin size={22} />
                <h5>Login</h5>
              </NavLink>
            )
          )}
        </div>
      </div>



      <ul className={isChecked ? "show" : ""}>
        <IoMdClose className="IoMdClose" size={30} onClick={handleMenuClose} />

        <NavLink to="/home" onClick={handleMenuClose}>
          <li>Home</li>
        </NavLink>
        <NavLink to="/profile" onClick={handleMenuClose}>
          <li>Profile</li>
        </NavLink>
        <NavLink to="/about" onClick={handleMenuClose}>
          <li>About Us</li>
        </NavLink>
        <NavLink to="/privacy" onClick={handleMenuClose}>
          <li>Privacy Policy</li>
        </NavLink>
        <NavLink to="/legal" onClick={handleMenuClose}>
          <li>Legal Documents</li>
        </NavLink>

        <NavLink to="/Chat" onClick={handleMenuClose}>
          <li>Chat With Admin</li>
        </NavLink>

        {/* <NavLink to="/Status" onClick={handleMenuClose}>
          <li>Status</li>
        </NavLink> */}
        {/* <NavLink to="/Setpin"  onClick={handleMenuClose} >
          <li>Set Pin Activation Pin</li>
        </NavLink> */}
        <NavLink to="/change_password" onClick={handleMenuClose}>
          <li>Change Password</li>
        </NavLink>
        <NavLink to="/ContactUs" onClick={handleMenuClose}>
          <li>Contact Us</li>
        </NavLink>
        <a
          href="https://chat.whatsapp.com/HxI0Iy8PcZyAu8dy85ycmg"
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleMenuClose}
          style={{ marginBottom: "50px" }}
        >
          <li>Help</li>
        </a>
        <div className="userDetails">
          {!isVirtualMallPage && (
            <>
              <CgProfile size={40} />
              <div className="login-details">
                <h5>Referral Id: {referralId}</h5>
                <h5>Sponser Id: {sponserId}</h5>
                <h5>User Name: {userName}</h5>
              </div>
            </>
          )}
          <div className="login-details">
            {location.pathname === "/home" && (
              <h5 onClick={logOut} id="log">
                Log Out
              </h5>
            )}
          </div>
        </div>
      </ul>
    </div>
  );
};

export default Header;
